/**
 * API服务层
 */

import axios, { AxiosResponse } from 'axios';

// API 基础配置
// 优先使用环境变量，否则回退到相对路径 '/api' 以利用 Vite Proxy
const getApiBaseUrl = () => {
  // Always use relative path to ensure proxying works correctly in both Dev (Vite) and Prod (Nginx)
  return '/api';
};
const API_BASE_URL = getApiBaseUrl();

// 创建axios实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    // 可以在这里添加认证token等
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 安全性：不在请求头透传 Tushare Token，避免第三方服务日志泄露
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // 统一错误处理
    if (error.response?.status === 401) {
      // 处理认证失败
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 价格数据接口
export interface PriceData {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  amount?: number;
}

// 技术指标数据接口
export interface TechnicalIndicatorData {
  timestamp: string;
  indicator: string;
  value: number;
  signal?: string;
  [key: string]: string | number | undefined;
}

// 性能数据接口
export interface PerformanceData {
  timestamp: string;
  returns: number;
  cumulative_returns: number;
  volatility: number;
  sharpe_ratio?: number;
}

// 风险数据接口
export interface RiskData {
  timestamp: string;
  var: number;
  cvar: number;
  max_drawdown: number;
  beta?: number;
}

// 投资组合数据接口
export interface PortfolioData {
  timestamp: string;
  position: number;
  value: number;
  weight: number;
  pnl: number;
}

// 策略数据接口
export interface StrategyData {
  name: string;
  signal: string;
  confidence: number;
  entry_price?: number;
  exit_price?: number;
  returns?: number;
  analysis_details?: any; // Added to support detailed strategy data like spread analysis
  reason?: string;
  strategy_type?: string;
}

// 股票搜索接口
export interface StockInfo {
  symbol: string;
  name: string;
  market: string;
  price?: number;
  change?: number;
  changePercent?: number;
}

// 分析请求参数
export interface AnalysisRequest {
  symbol: string;
  start_date?: string;
  end_date?: string;
  strategies?: string[];
  timeframe?: string;
  [key: string]: string | string[] | undefined;
}

// 分析响应数据
export interface AnalysisResponse {
  data: {
    price_data: PriceData[];
    technical_indicators: TechnicalIndicatorData[];
    performance_data: PerformanceData[];
    risk_data: RiskData[];
    portfolio_data: PortfolioData[];
    strategies: StrategyData[];
    // 添加overview相关字段以支持PDF导出一致性
    overall_signal?: string;
    confidence?: number;
    risk_level?: string;
    expected_return?: number;
    expected_returns_periods?: { [key: string]: number };
    expected_return_weighted?: number;
    expected_returns_periods_weighted?: { [key: string]: number };
    [key: string]: any;
  };
}

// 股票数据响应
export interface StockDataResponse {
  data: {
    price_data: PriceData[];
    technical_indicators: TechnicalIndicatorData[];
    [key: string]: PriceData[] | TechnicalIndicatorData[];
  };
}

// 导出请求参数
export interface ExportRequest {
  symbol: string;
  data: AnalysisResponse['data'] | StockDataResponse['data'];
  format?: 'pdf' | 'excel';
  lang?: string;
}

// 技术指标结果
export interface TechnicalIndicatorResult {
  value: number;
  signal: string;
  strength: number;
  description: string;
}

// 技术分析响应
export interface TechnicalAnalysisResponse {
  symbol: string;
  timestamp: string;
  technical_indicators: {
    [key: string]: TechnicalIndicatorResult;
  };
  market_analysis: {
    symbol: string;
    analysis_time: string;
    trend_direction: string;
    trend_strength: number;
    risk_level: string;
    volatility: number;
    support_levels: number[];
    resistance_levels: number[];
    price_targets: {
      short_term: number;
      medium_term: number;
      long_term: number;
    };
    confidence_score: number;
    key_insights: string[];
    recommendations: string[];
  };
  status: string;
}

// 技术分析请求参数
export interface TechnicalAnalysisRequest {
  symbol: string;
  market?: string;
  period?: string;
  days?: number;
}

// 支付接口类型定义
export interface PaymentCreateRequest {
  symbol: string;
  price?: number;
}

export interface PaymentCreateResponse {
  order_id: string;
  qr_image_base64: string;
  qr_image_url: string;
  expire_at: string;
  amount: number;
}

export interface PaymentStatusResponse {
  order_id: string;
  status: 'pending' | 'paid' | 'expired' | 'failed';
  paid: boolean;
}

export interface OrderEvent {
  timestamp: string;
  order_id: string;
  symbol: string;
  event: string;
  payload: string | null;
  amount: number;
}

export interface SalesStats {
  count: number;
  amount: number;
}

export interface SalesStatisticsResponse {
  today: SalesStats;
  week: SalesStats;
  month: SalesStats;
  year: SalesStats;
  total: SalesStats;
}

export interface StockPopularityResponse {
  symbol: string;
  count: number;
}

// API服务类
class AnalysisApi {
  // 登录接口
  async login(username: string, password: string): Promise<{ access_token: string; token_type: string }> {
    // 显式使用 qs 风格的字符串，避免 axios 自动 JSON 序列化
    // 必须使用 application/x-www-form-urlencoded
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    
    // 注意：这里不能直接传 URLSearchParams 对象给 axios.post 的第二个参数，
    // 否则 axios 可能会自动移除 Content-Type header 或者处理不当。
    // 最稳妥的方式是直接传 toString() 后的字符串。
    const response = await apiClient.post('/auth/token', formData.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  }

  // 获取当前用户信息
  async getCurrentUser(): Promise<any> {
    const response = await apiClient.get('/auth/users/me');
    return response.data;
  }

  // 搜索股票
  async searchStocks(keyword: string): Promise<StockInfo[]> {
    try {
      const response = await apiClient.post('/stocks/search', {
        query: keyword,
        limit: 10
      });
      
      // 处理后端返回的数据格式
      const stocks = response.data || [];
      
      return stocks.map((stock: Record<string, unknown>) => ({
        symbol: (stock.symbol || stock.code || '') as string,
        name: (stock.name || stock.stock_name || '') as string,
        market: (stock.market || 'A') as string,
        price: (stock.price || stock.current_price || 0) as number,
        change: (stock.change || 0) as number,
        changePercent: (stock.change_percent || stock.changePercent || 0) as number
      }));
    } catch (error) {
      console.error('Search stocks error:', error);
      return [];
    }
  }

  // 获取后端策略状态（动态列表/权重/启用情况）
  async getStrategyStatus(): Promise<{ strategies: Record<string, { enabled?: boolean; weight?: number; ready?: boolean; name?: string }>; summary?: any } | null> {
    try {
      const response = await apiClient.get('/stocks/strategies/status', { timeout: 2000 });
      const data = response.data || {};
      const strategies = (data.strategies || data.strategy_status || {}) as Record<string, any>;
      const summary = data.summary || data.initialization_summary || data;
      return { strategies, summary };
    } catch (_) {
      return null;
    }
  }

  // 更新后端策略权重（0-1 区间，权重总和必须为1.0）
  async updateStrategyWeights(weights01: Record<string, number>): Promise<{ message?: string; weights?: Record<string, number> } | null> {
    try {
      const response = await apiClient.post('/stocks/strategies/weights', weights01);
      return response.data || null;
    } catch (err) {
      console.error('Update strategy weights error:', err);
      return null;
    }
  }

  // 获取股票数据 - 通过后端API
  async getStockData(symbol: string): Promise<StockDataResponse> {
    try {
      // 并行获取实时数据和历史数据
      const [realtimeResponse, historicalResponse] = await Promise.allSettled([
        apiClient.get(`/stocks/realtime/${encodeURIComponent(symbol)}`),
        apiClient.post('/stocks/historical', {
          symbol: symbol,
          start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 365天前
          end_date: new Date().toISOString().split('T')[0],
          period: '1d'
        })
      ]);
      
      // 处理实时数据
      const realtimeData = realtimeResponse.status === 'fulfilled' ? realtimeResponse.value.data : null;
      
      // 处理历史数据
      const historicalData = historicalResponse.status === 'fulfilled' ? historicalResponse.value.data : null;
      
      // 转换为前端期望的格式
      return {
        data: {
          // 兼容后端两种返回结构：ETF路径使用 price_data，常规路径使用 data
          price_data: this.convertPriceData((historicalData?.price_data as any) || (historicalData?.data as any) || []),
          technical_indicators: this.convertQuoteToIndicators(realtimeData)
        }
      };
    } catch (error) {
      console.error('Get stock data error:', error);
      // 发生错误时返回空数据
      return {
        data: {
          price_data: [],
          technical_indicators: []
        }
      };
    }
  }

  // 运行量化分析 - 通过后端API
  async runAnalysis(request: AnalysisRequest): Promise<AnalysisResponse> {
    try {
      // 使用统一策略分析端点，支持配对交易与权重/阈值/启用列表
      const params = new URLSearchParams();
      // 市场、周期与历史天数采用默认值以适应后端策略需求
      params.set('market', 'A');
      params.set('period', '1d');
      // 若启用手动配对，按左栏回看周期动态扩大历史天数（lookback+30，最低365，最高720）
      let paired = (request as any).pairedSymbol as string | undefined;
      try {
        if (paired && /^\d{6}$/.test(paired.trim())) {
          const s = paired.trim();
          paired = `${s}.${s.startsWith('6') ? 'SH' : 'SZ'}`;
        }
      } catch { /* ignore */ }
      try {
        if (paired && paired.trim()) {
          const lbRaw = (typeof localStorage !== 'undefined') ? localStorage.getItem('STAT_ARB_LOOKBACK') : null;
          const lb = lbRaw ? parseInt(lbRaw) : 365;
          const daysVal = Math.max(365, Math.min(lb + 30, 720));
          params.set('days', String(daysVal));
        } else {
          params.set('days', '180');
        }
      } catch {
        params.set('days', paired && paired.trim() ? '365' : '180');
      }
      // 启用策略列表
      const stRaw = (request as any).strategies;
      if (Array.isArray(stRaw) && stRaw.length > 0) {
        const names: string[] = [];
        for (const it of stRaw as any[]) {
          if (typeof it === 'string') {
            names.push(it);
          } else if (it && typeof it === 'object') {
            const cand = (it.type || it.strategy_type || it.name || '').toString().trim();
            if (cand) names.push(cand);
          }
        }
        if (names.length > 0) {
          params.set('strategies', names.join(','));
        }
      }
      // 配对股票（统计套利）与参数透传
      // 即使没有paired_symbol，如果用户修改了z_entry等参数，也应该传递
      // 优先使用传入的参数，其次查localStorage
      const zEntry = (request as any).stat_arb_z_entry ?? ((typeof localStorage !== 'undefined') ? localStorage.getItem('STAT_ARB_Z_ENTRY') : null);
      const zExit = (request as any).stat_arb_z_exit ?? ((typeof localStorage !== 'undefined') ? localStorage.getItem('STAT_ARB_Z_EXIT') : null);
      const lookback = (request as any).stat_arb_lookback ?? ((typeof localStorage !== 'undefined') ? localStorage.getItem('STAT_ARB_LOOKBACK') : null);

      if (zEntry) params.set('stat_arb_z_entry', String(zEntry));
      if (zExit) params.set('stat_arb_z_exit', String(zExit));
      if (lookback) params.set('stat_arb_lookback', String(lookback));

      if (paired && paired.trim()) {
        params.set('paired_symbol', paired.trim());
      }
      // 可选阈值（若前端提供则透传）
      const tau = (request as any).tau; const alpha = (request as any).alpha; const beta = (request as any).beta;
      if (tau !== undefined) params.set('tau', String(tau));
      if (alpha !== undefined) params.set('alpha', String(alpha));
      if (beta !== undefined) params.set('beta', String(beta));
      
      // GET 请求到 /analysis/strategy/{symbol}
      const response = await apiClient.get(`/analysis/strategy/${encodeURIComponent(request.symbol)}?${params.toString()}`);
      // 后端返回的数据已经是统一结构
      const analysisData = response.data;
      
      // 转换为前端期望的格式，包含overview相关字段
      return {
        data: {
          price_data: analysisData.price_data || [],
          technical_indicators: analysisData.technical_indicators || [],
          performance_data: analysisData.performance_data || [],
          risk_data: analysisData.risk_data || [],
          portfolio_data: analysisData.portfolio_data || [],
          strategies: analysisData.strategies || [],
          // 添加overview相关字段以支持PDF导出一致性
          overall_signal: analysisData.overall_signal,
          confidence: analysisData.confidence,
          risk_level: analysisData.risk_level,
          expected_return: analysisData.expected_return,
          expected_returns_periods: analysisData.expected_returns_periods,
          expected_return_weighted: analysisData.expected_return_weighted,
          expected_returns_periods_weighted: analysisData.expected_returns_periods_weighted,
          investment_summary: analysisData.investment_summary // 透传投资摘要
        }
      };
    } catch (error) {
      console.error('Run analysis error:', error);
      // 重新抛出错误，让前端组件能够捕获并显示错误信息
      throw error;
    }
  }

  // 获取回测结果
  async runBacktest(body: any): Promise<any> {
    try {
      const response = await apiClient.post('/backtest/run', body, { responseType: 'json' });
      return response.data;
    } catch (error) {
      console.error('Run backtest error:', error);
      throw error;
    }
  }

  // 获取技术分析
  async getTechnicalAnalysis(request: TechnicalAnalysisRequest): Promise<TechnicalAnalysisResponse> {
    try {
      const params = new URLSearchParams();
      if (request.market) params.append('market', request.market);
      if (request.period) params.append('period', request.period);
      if (request.days) params.append('days', request.days.toString());

      const response = await apiClient.get(`/analysis/technical/${request.symbol}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Technical analysis error:', error);
      throw error;
    }
  }

  // 获取策略分析数据
  async getStrategyAnalysis(symbol: string, market: string = 'A', period: string = '1d', days: number = 300, paired_symbol?: string): Promise<any> {
    try {
      const response = await apiClient.get(`/analysis/strategy/${symbol}`, {
        params: {
          market,
          period,
          days,
          paired_symbol
        }
      });
      return response.data;
    } catch (error) {
      console.error('Strategy analysis error:', error);
      throw error;
    }
  }

  async getRealtimeData(symbol: string): Promise<Record<string, any>> {
    try {
      const response = await apiClient.get(`/stocks/realtime/${encodeURIComponent(symbol)}`);
      return response.data || {};
    } catch (error) {
      return {};
    }
  }

  // 导出报告
  async exportReport(request: ExportRequest): Promise<AxiosResponse> {
    try {
      const response = await apiClient.post('/analysis/export', request, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Export report error:', error);
      throw error;
    }
  }

  // 注意：已移除所有模拟数据生成方法，系统严格禁止使用模拟数据
  // 所有数据必须来自真实的市场数据源

  // 新增：转换价格数据格式
  private convertPriceData(priceData: unknown[]): PriceData[] {
    if (!priceData || !Array.isArray(priceData)) return [];
    
    return priceData.map((item: unknown) => {
      const data = item as Record<string, unknown>;
      return {
        timestamp: String(data.date || data.timestamp || new Date().toISOString().split('T')[0]),
        open: parseFloat(String(data.open)) || 0,
        high: parseFloat(String(data.high)) || 0,
        low: parseFloat(String(data.low)) || 0,
        close: parseFloat(String(data.close)) || 0,
        volume: parseFloat(String(data.volume)) || 0,
        amount: parseFloat(String(data.amount)) || 0
      };
    });
  }

  // 新增：转换K线数据格式
  private convertKlineData(klineData: { data?: unknown[] }): PriceData[] {
    if (!klineData || !Array.isArray(klineData.data)) return [];
    
    return klineData.data.map((item: unknown) => {
      const data = item as Record<string, unknown>;
      return {
        timestamp: String(data.timestamp || data.date || new Date().toISOString().split('T')[0]),
        open: parseFloat(String(data.open)) || 0,
        high: parseFloat(String(data.high)) || 0,
        low: parseFloat(String(data.low)) || 0,
        close: parseFloat(String(data.close)) || 0,
        volume: parseFloat(String(data.volume)) || 0
      };
    });
  }

  // 新增：将报价数据转换为技术指标格式
  private convertQuoteToIndicators(quoteData: Record<string, unknown>): TechnicalIndicatorData[] {
    if (!quoteData) return [];
    
    const timestamp = new Date().toISOString().split('T')[0];
    return [{
      timestamp,
      indicator: 'quote',
      value: parseFloat(String((quoteData as any).current_price || (quoteData as any).close || 0)) || 0,
      signal: 'neutral',
      ma5: parseFloat(String((quoteData as any).ma5)) || 0,
      ma20: parseFloat(String((quoteData as any).ma20)) || 0,
      ma50: parseFloat(String((quoteData as any).ma50)) || 0,
      rsi: parseFloat(String((quoteData as any).rsi)) || 0,
      macd: parseFloat(String((quoteData as any).macd)) || 0
    }];
  }

  // 新增：转换技术指标数据格式
  private convertTechnicalIndicators(technicalIndicators: unknown): TechnicalIndicatorData[] {
    if (!technicalIndicators) return [];
    
    // 将后端的技术指标数据转换为前端期望的格式
    const indicators: TechnicalIndicatorData[] = [];
    
    // 如果后端返回的是对象格式的技术指标
    if (typeof technicalIndicators === 'object' && !Array.isArray(technicalIndicators)) {
      const data = technicalIndicators as Record<string, unknown>;
      const timestamp = new Date().toISOString().split('T')[0];
      indicators.push({
        timestamp,
        indicator: 'technical',
        value: parseFloat(String(data.close)) || 0,
        signal: 'neutral',
        ma5: parseFloat(String(data.ma5)) || 0,
        ma20: parseFloat(String(data.ma20)) || 0,
        ma50: parseFloat(String(data.ma50)) || 0,
        rsi: parseFloat(String(data.rsi)) || 0,
        macd: parseFloat(String(data.macd)) || 0
      });
    }
    // 如果后端返回的是数组格式
    else if (Array.isArray(technicalIndicators)) {
      return technicalIndicators.map((item: unknown) => {
        const data = item as Record<string, unknown>;
        return {
          timestamp: String(data.timestamp || data.date || new Date().toISOString().split('T')[0]),
          indicator: 'technical',
          value: parseFloat(String(data.value || data.close)) || 0,
          signal: 'neutral',
          ma5: parseFloat(String(data.ma5)) || 0,
          ma20: parseFloat(String(data.ma20)) || 0,
          ma50: parseFloat(String(data.ma50)) || 0,
          rsi: parseFloat(String(data.rsi)) || 0,
          macd: parseFloat(String(data.macd)) || 0
        };
      });
    }
    
    return indicators;
  }

  // 新增：转换风险数据格式
  private convertRiskData(recommendation: Record<string, unknown>): RiskData[] {
    if (!recommendation) {
      console.warn('No recommendation data available for risk analysis');
      return [];
    }
    
    const now = new Date();
    const riskData: RiskData[] = [];
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const riskLevel = String(recommendation.risk_level);
      riskData.unshift({
        timestamp: date.toISOString().split('T')[0],
        var: riskLevel === 'high' ? 0.8 + Math.random() * 0.2 : 
             riskLevel === 'medium' ? 0.4 + Math.random() * 0.4 : 
             0.1 + Math.random() * 0.3,
        cvar: 0.05 + Math.random() * 0.1,
        max_drawdown: Math.random() * 20,
        beta: 0.8 + Math.random() * 0.4
      });
    }
    
    return riskData;
  }

  // 新增：转换策略数据格式
  private convertStrategies(individualStrategies: unknown[]): StrategyData[] {
    if (!individualStrategies || !Array.isArray(individualStrategies)) {
      console.warn('No strategy data available');
      return [];
    }
    
    return individualStrategies.map((strategy: unknown, index: number) => {
      const data = strategy as Record<string, unknown>;
      const riskLevel = String(data.risk_level);
      return {
        name: String(data.strategy_name || `策略 ${index + 1}`),
        signal: String(data.signal || 'neutral'),
        confidence: parseFloat(String(data.confidence)) || 0.5 + Math.random() * 1.5,
        entry_price: parseFloat(String(data.entry_price)) || 0,
        exit_price: parseFloat(String(data.exit_price)) || 0,
        returns: parseFloat(String(data.expected_return)) || Math.random() * 0.2 - 0.1
      };
    });
  }

  // 新增：从K线数据生成性能数据
  private generatePerformanceFromData(klineData: { data?: unknown[] }): PerformanceData[] {
    if (!klineData || !Array.isArray(klineData.data) || klineData.data.length === 0) {
      console.warn('No K-line data available for performance analysis');
      return [];
    }
    
    const data = klineData.data;
    const returns: PerformanceData[] = [];
    
    for (let i = 1; i < Math.min(data.length, 30); i++) {
      const prevData = data[i-1] as Record<string, unknown>;
      const currentData = data[i] as Record<string, unknown>;
      const prevClose = parseFloat(String(prevData.close)) || 0;
      const currentClose = parseFloat(String(currentData.close)) || 0;
      const dailyReturn = prevClose > 0 ? (currentClose - prevClose) / prevClose : 0;
      const cumulativeReturn = returns.length > 0 ? (1 + dailyReturn) * (1 + returns[returns.length - 1].cumulative_returns / 100) - 1 : dailyReturn;
      
      returns.push({
        timestamp: String(currentData.timestamp || currentData.date || new Date().toISOString().split('T')[0]),
        returns: dailyReturn * 100,
        cumulative_returns: cumulativeReturn * 100,
        volatility: Math.abs(dailyReturn) * 100
      });
    }
    
    return returns;
  }

  // 新增：从报价和资金流数据生成风险数据
  private generateRiskFromData(quoteData: Record<string, unknown>, capitalFlowData: Record<string, unknown>): RiskData[] {
    if (!quoteData) {
      console.warn('No quote data available for risk analysis');
      return [];
    }
    
    const volatility = Math.abs((parseFloat(String(quoteData.change_percent)) || 0) / 100) * 10; // 简单波动率估算
    const beta = parseFloat(String(capitalFlowData?.beta)) || 1.0;
    
    return [{
      timestamp: new Date().toISOString().split('T')[0],
      var: volatility * 2.33, // 99% VaR
      cvar: volatility * 3.0,
      max_drawdown: volatility * 20,
      beta: beta
    }];
  }

  // 新增：从报价数据生成投资组合数据
  private generatePortfolioFromData(quoteData: Record<string, unknown>): PortfolioData[] {
    if (!quoteData) {
      console.warn('No quote data available for portfolio analysis');
      return [];
    }
    
    const currentPrice = parseFloat(String(quoteData.current || quoteData.close)) || 0;
    const change = parseFloat(String(quoteData.change)) || 0;
    
    return [{
      timestamp: new Date().toISOString().split('T')[0],
      position: 1000,
      value: currentPrice * 1000,
      weight: 1.0,
      pnl: change * 1000
    }];
  }

  // 新增：从指标数据生成策略数据
  private generateStrategiesFromData(indicatorData: Record<string, unknown>): StrategyData[] {
    if (!indicatorData) {
      console.warn('No indicator data available for strategy analysis');
      return [];
    }
    
    // 基于真实指标数据生成策略建议
    const strategies: StrategyData[] = [];
    
    // 趋势跟踪策略
    strategies.push({
      name: 'trend_following',
      signal: 'neutral',
      confidence: 0.8 + Math.random() * 0.8,
      returns: Math.random() * 0.15 - 0.05
    });
    
    return strategies;
  }

  // === 主动推荐接口（ESG + Smart Beta） ===
  // 推荐项数据结构
  async getRecommendations(params: {
    strategy?: 'esg_low_vol' | 'esg_high_div' | 'esg_combo';
    indices?: string; // 逗号分隔，如 '000300.SH,000905.SH,000852.SH'
    top?: number;
    lookback_days?: number;
    vol_upper?: number;
    div_upper?: number;
    robust_dividend?: boolean;
    full_scan?: boolean;
  }): Promise<{
    timestamp: string;
    strategy: string;
    indices: string[];
    count: number;
    items: Array<{
      symbol: string;
      market: string;
      esg_score: number;
      volatility?: number | null;
      dividend_yield?: number | null;
      composite_score: number;
      reasons: string[];
    }>;
    details?: any;
  }> {
    const response = await apiClient.get('/recommendations', { params });
    return response.data;
  }

  // 应用ESG评分（将指数成分转换为评分并注入后端权重评估器）
  async applyEsgScores(indexCodes: string): Promise<{ applied?: boolean; count?: number }> {
    const response = await apiClient.get('/esg/scores', { params: { index_codes: indexCodes, apply: true } });
    return response.data || {};
  }

  // 通过URL下载并解析指数成分/权重，注入评分
  async ingestUrl(url: string, equalWeightScore = 0.80): Promise<{ applied?: boolean; count?: number; source?: string }> {
    const response = await apiClient.post('/esg/indexes/ingest_url', null, { params: { url, equal_weight_score: equalWeightScore } });
    return response.data || {};
  }

  // 从本机绝对路径读取txt/csv并注入评分（无需HTTP下载）
  async ingestLocal(filePath: string, equalWeightScore = 0.80, indexCode?: string): Promise<{ applied?: boolean; count?: number; source?: string }> {
    const params: any = { file_path: filePath, equal_weight_score: equalWeightScore };
    if (indexCode) params.index_code = indexCode;
    const response = await apiClient.post('/esg/indexes/ingest_local', null, { params });
    return response.data || {};
  }

  // 解析并注入：CSI ESG指数（如 931463、931686）
  async ingestCsi(indexCode: string): Promise<any> {
    const response = await apiClient.get('/esg/indexes/ingest_csi', { params: { index_code: indexCode } });
    return response.data;
  }

  // 解析并注入：SSE 科创主题指数（如 000680、000699）
  async ingestSse(indexCode: string): Promise<any> {
    const response = await apiClient.get('/esg/indexes/ingest_sse', { params: { index_code: indexCode } });
    return response.data;
  }

  // 上传纯文本：code[,weight] 按行解析注入评分
  async uploadTxt(content: string, equal_weight_score?: number): Promise<any> {
    const response = await apiClient.post('/esg/indexes/upload_txt', null, { params: { content, equal_weight_score } });
    return response.data;
  }

  // === 支付相关接口 ===
  async createPayment(request: PaymentCreateRequest): Promise<PaymentCreateResponse> {
    const response = await apiClient.post('/payments/create', request);
    return response.data;
  }

  async checkPaymentStatus(orderId: string): Promise<PaymentStatusResponse> {
    const response = await apiClient.get('/payments/status', { params: { order_id: orderId } });
    return response.data;
  }

  async getOrderEvents(limit: number = 50): Promise<{ events: OrderEvent[] }> {
    const response = await apiClient.get('/payments/events', { params: { limit } });
    return response.data;
  }

  async getSalesStatistics(): Promise<SalesStatisticsResponse> {
    const response = await apiClient.get('/payments/stats/sales');
    return response.data;
  }

  async getStockPopularity(limit: number = 10): Promise<StockPopularityResponse[]> {
    const response = await apiClient.get('/payments/stats/popularity', { params: { limit } });
    return response.data;
  }

  // === 系统设置 ===
  async getSystemSettings(): Promise<Record<string, { value: string, description: string, updated_at: string }>> {
    const response = await apiClient.get('/settings');
    return response.data;
  }

  async updateSystemSettings(settings: { key: string, value: string }[]): Promise<{ success: boolean }> {
    const response = await apiClient.post('/settings', { settings });
    return response.data;
  }

  // === 用户管理 & 审计 ===
  async getAllUsers(): Promise<any[]> {
    const response = await apiClient.get('/auth/users');
    return response.data;
  }

  async createUser(data: any): Promise<{ success: boolean }> {
    const response = await apiClient.post('/auth/users', data);
    return response.data;
  }

  async updateMyPassword(data: any): Promise<{ success: boolean }> {
    const response = await apiClient.put('/auth/users/me/password', data);
    return response.data;
  }

  async getAuditLogs(limit: number = 100): Promise<any[]> {
    const response = await apiClient.get('/settings/audit_logs', { params: { limit } });
    return response.data;
  }
}

// 导出API实例
export const analysisApi = new AnalysisApi();

// 导出默认实例
export default analysisApi;
